import React, { useState, useEffect, useCallback, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';

/**
 * FeedEngine - Universal Nested Feed Component
 * 
 * Renders vertical social feed with:
 * - Swipe/scroll/keyboard navigation
 * - Caption expansion (MORE/LESS)
 * - CTA action dispatcher
 * - Back navigation for child feeds
 * - Media performance (video pause/play)
 * - Loop support
 * 
 * Works in both desktop phone container and mobile fullscreen
 */
export default function FeedEngine({
  currentFeed,
  currentIndex,
  onNavigateNext,
  onNavigatePrev,
  onSetIndex,
  onOpenChildFeed,
  onGoBack,
  canGoBack,
  parentFeed,
  hideChrome = false // mobile renders logo + back in MobileHeader instead
}) {
  // Initialize all hooks FIRST
  const [expandedCaption, setExpandedCaption] = useState(false);
  const [direction, setDirection] = useState(0);
  const [touchStart, setTouchStart] = useState(0);
  const [touchEnd, setTouchEnd] = useState(0);
  const containerRef = useRef(null);
  const isScrolling = useRef(false);
  const videoRef = useRef(null);

  // Derive values (safe to be conditional internally)
  const currentPost = currentFeed?.posts?.[currentIndex];
  const totalPosts = currentFeed?.posts?.length || 0;

  // ============================================================================
  // NAVIGATION HANDLERS
  // ============================================================================

  const handleNext = useCallback(() => {
    if (isScrolling.current) return;
    
    isScrolling.current = true;
    setDirection(1);
    setExpandedCaption(false); // Collapse caption on swipe
    
    // INFINITE LOOP: Wrap to beginning after last post
    const nextIndex = (currentIndex + 1) % totalPosts;
    onSetIndex(nextIndex);
    
    setTimeout(() => {
      isScrolling.current = false;
    }, 600);
  }, [currentIndex, totalPosts, onSetIndex]);

  const handlePrev = useCallback(() => {
    if (isScrolling.current) return;
    
    isScrolling.current = true;
    setDirection(-1);
    setExpandedCaption(false); // Collapse caption on swipe
    
    // INFINITE LOOP: Wrap to end when going backward from first post
    const prevIndex = (currentIndex - 1 + totalPosts) % totalPosts;
    onSetIndex(prevIndex);
    
    setTimeout(() => {
      isScrolling.current = false;
    }, 600);
  }, [currentIndex, totalPosts, onSetIndex]);

  const handleNavigateToIndex = useCallback((index) => {
    if (index === currentIndex) return;
    setDirection(index > currentIndex ? 1 : -1);
    setExpandedCaption(false);
    onSetIndex(index);
  }, [currentIndex, onSetIndex]);

  // ============================================================================
  // INPUT HANDLERS (Mouse wheel, keyboard, touch)
  // ============================================================================

  // Mouse wheel navigation; handleNext/handlePrev own the throttle, so never set isScrolling here
  const handleWheel = useCallback((e) => {
    if (isScrolling.current || expandedCaption) return;
    if (Math.abs(e.deltaY) < 30) return; // ignore trackpad micro-scrolls

    if (e.deltaY > 0) {
      handleNext();
    } else {
      handlePrev();
    }
  }, [expandedCaption, handleNext, handlePrev]);

  const handleKeyDown = useCallback((e) => {
    if (isScrolling.current) return;

    // Allow arrow keys only if caption is collapsed
    if (e.key === 'ArrowDown' && !expandedCaption) {
      e.preventDefault();
      handleNext();
    } else if (e.key === 'ArrowUp' && !expandedCaption) {
      e.preventDefault();
      handlePrev();
    }
  }, [expandedCaption, handleNext, handlePrev]);

  const handleTouchStart = useCallback((e) => {
    setTouchStart(e.targetTouches[0].clientY);
    setTouchEnd(e.targetTouches[0].clientY);
  }, []);

  const handleTouchMove = useCallback((e) => {
    setTouchEnd(e.targetTouches[0].clientY);
  }, []);

  const handleTouchEnd = useCallback(() => {
    if (isScrolling.current || expandedCaption) return;

    const distance = touchStart - touchEnd;
    const threshold = 80;

    if (Math.abs(distance) > threshold) {
      if (distance > 0) {
        handleNext();
      } else {
        handlePrev();
      }
    }
  }, [touchStart, touchEnd, expandedCaption, handleNext, handlePrev]);

  // ============================================================================
  // EFFECTS
  // ============================================================================

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    container.addEventListener('wheel', handleWheel, { passive: false });
    return () => container.removeEventListener('wheel', handleWheel);
  }, [handleWheel]);

  useEffect(() => {
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [handleKeyDown]);

  // Pause previous video, play current (immediate on swipe)
  useEffect(() => {
    const videos = document.querySelectorAll('video');
    videos.forEach(v => {
      v.pause();
      v.currentTime = 0;
    });
    
    if (videoRef.current && currentPost?.media?.type === 'video') {
      videoRef.current.play().catch(err => {
        console.warn('Video autoplay blocked, user interaction required:', err);
      });
    }
  }, [currentIndex, currentPost]);

  // Collapse caption on post change
  useEffect(() => {
    setExpandedCaption(false);
  }, [currentIndex]);

  // ============================================================================
  // CTA HANDLER
  // ============================================================================

  const handleCTA = useCallback(() => {
    if (!currentPost?.cta) return;

    const { type, destination } = currentPost.cta;

    if (type === 'CHILD_FEED' && destination && onOpenChildFeed) {
      onOpenChildFeed(destination);
    } else if (type === 'EXTERNAL_LINK' && destination) {
      window.open(destination, '_blank', 'noopener,noreferrer');
    }
  }, [currentPost, onOpenChildFeed]);

  // ============================================================================
  // RENDER
  // ============================================================================

  if (!currentPost || !currentFeed) {
    return (
      <div className="w-full h-screen flex items-center justify-center bg-[#0A0A0F] text-white">
        <p className="text-sm">Loading...</p>
      </div>
    );
  }

  // Determine if MORE should be shown (only if there's additional content)
  const hasMoreContent = currentPost.fullInformation && 
                        currentPost.fullInformation !== currentPost.shortCaption && 
                        currentPost.fullInformation.length > currentPost.shortCaption.length;

  // Determine if EXPLORE should be shown (CTA exists with valid destination)
  const hasExplore = currentPost.cta && 
                    currentPost.cta.type !== 'NONE' && 
                    currentPost.cta.destination;

  return (
    <div
      ref={containerRef}
      className="relative w-full h-screen overflow-hidden bg-[#0A0A0F]"
      onTouchStart={handleTouchStart}
      onTouchMove={handleTouchMove}
      onTouchEnd={handleTouchEnd}
      data-testid="feed-engine"
    >
      {/* Back Button (nested feeds only) */}
      {canGoBack && !hideChrome && (
        <motion.button
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          onClick={onGoBack}
          className="absolute top-6 left-6 z-50 flex items-center gap-2 px-4 py-2 text-xs font-semibold uppercase tracking-wider text-white/60 hover:text-white transition-colors duration-200"
          data-testid="back-button"
        >
          <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M15 19l-7-7 7-7" />
          </svg>
          <span>{parentFeed?.name || 'BACK'}</span>
        </motion.button>
      )}

      {/* Grain Texture Overlay */}
      <div 
        className="absolute inset-0 pointer-events-none opacity-[0.02] z-40 mix-blend-overlay"
        style={{
          backgroundImage: `url("data:image/svg+xml,%3Csvg viewBox='0 0 400 400' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='noiseFilter'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='4' /%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23noiseFilter)' /%3E%3C/svg%3E")`,
          backgroundRepeat: 'repeat',
          backgroundSize: '180px'
        }}
      />

      {/* Feed Post */}
      <AnimatePresence mode="wait" custom={direction}>
        <motion.div
          key={`${currentFeed.id}-${currentIndex}`}
          custom={direction}
          initial={{ y: direction > 0 ? '100%' : '-100%', opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          exit={{ y: direction > 0 ? '-100%' : '100%', opacity: 0 }}
          transition={{
            duration: 0.6,
            ease: [0.16, 1, 0.3, 1]
          }}
          className="absolute inset-0"
          data-testid={`feed-item-${currentPost.id}`}
        >
          {/* Background Media with Parallax */}
          <motion.div
            className="absolute inset-0"
            initial={{ scale: 1.1 }}
            animate={{ scale: 1 }}
            transition={{ duration: 0.8, ease: "easeOut" }}
          >
            {currentPost.media.type === 'image' ? (
              <img
                src={currentPost.media.url}
                alt={currentPost.media.alt}
                className="w-full h-full object-cover"
                loading="lazy"
              />
            ) : (
              <video
                ref={videoRef}
                src={currentPost.media.url}
                poster={currentPost.media.poster}
                className="w-full h-full object-cover"
                preload="metadata"
                loop
                muted
                playsInline
                autoPlay
                webkit-playsinline="true"
                x5-playsinline="true"
              />
            )}
            {/* Subtle bottom gradient for text readability only */}
            <div className="absolute inset-0 bg-gradient-to-b from-transparent via-transparent to-[#0A0A0F]/75" 
                 style={{ 
                   backgroundImage: 'linear-gradient(to bottom, transparent 0%, transparent 60%, rgba(10,10,15,0.75) 100%)' 
                 }} 
            />
          </motion.div>

          {/* Content Overlay */}
          <div className="absolute inset-0 flex flex-col justify-end px-6 sm:px-8 pt-20 pb-[calc(4rem+env(safe-area-inset-bottom))]">
            <motion.div
              initial={{ y: 30, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ duration: 0.5, delay: 0.15 }}
              className="max-w-md"
            >
              {/* Category Eyebrow */}
              <div 
                className="text-[10px] font-medium uppercase tracking-[0.2em] text-white/40 mb-2"
                style={{ fontFamily: "'SF Mono', monospace" }}
              >
                {currentPost.category}
              </div>

              {/* Title */}
              <h2 
                className="text-2xl sm:text-3xl font-bold tracking-tight leading-tight uppercase text-white mb-3 sm:mb-4"
                style={{ letterSpacing: '-0.02em', lineHeight: '1.1' }}
              >
                {currentPost.title}
              </h2>

              {/* Caption - Short or Expanded */}
              <div className="mb-4 sm:mb-6">
                <p className="text-sm font-normal text-white/60 leading-relaxed max-w-sm">
                  {expandedCaption ? currentPost.fullInformation : currentPost.shortCaption}
                </p>
                
                {/* MORE / LESS Button - Only show if there's actually more content */}
                {hasMoreContent && (
                  <button
                    onClick={() => setExpandedCaption(!expandedCaption)}
                    className="mt-3 text-xs font-semibold uppercase tracking-wider text-[#FF6B35] hover:text-[#FF8F66] transition-colors duration-200"
                    data-testid="toggle-caption-button"
                  >
                    {expandedCaption ? 'LESS' : 'MORE'}
                  </button>
                )}
              </div>

              {/* EXPLORE Button - Only show if CTA has destination */}
              {hasExplore && (
                <button
                  onClick={handleCTA}
                  data-testid="cta-button"
                  className="inline-flex items-center gap-2 px-6 sm:px-8 py-3 sm:py-4 text-sm font-semibold tracking-wide text-white bg-gradient-to-r from-[#FF6B35] to-[#FF8F66] rounded-full transition-all duration-300 hover:shadow-[0_0_32px_rgba(255,107,53,0.6)] hover:scale-[1.03] active:scale-[0.98]"
                >
                  <span>{currentPost.cta.label || 'EXPLORE'}</span>
                  <svg 
                    className="w-4 h-4 transition-transform duration-300 group-hover:translate-x-1" 
                    fill="none" 
                    viewBox="0 0 24 24" 
                    stroke="currentColor"
                    strokeWidth={2.5}
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" d="M17 8l4 4m0 0l-4 4m4-4H3" />
                  </svg>
                </button>
              )}
            </motion.div>
          </div>
        </motion.div>
      </AnimatePresence>

      {/* Progress Indicators - Mobile calibrated */}
      <div 
        className="absolute top-1/2 -translate-y-1/2 z-40 flex flex-col"
        style={{
          right: 'max(10px, env(safe-area-inset-right))',
          gap: '9px'
        }}
      >
        {currentFeed.posts.map((_, index) => (
          <button
            key={index}
            onClick={() => handleNavigateToIndex(index)}
            className="transition-all duration-300 p-0 border-0"
            style={{
              width: index === currentIndex ? '4px' : '5px',
              height: index === currentIndex ? '24px' : '5px',
              minWidth: index === currentIndex ? '4px' : '5px',
              minHeight: index === currentIndex ? '5px' : '5px',
              background: index === currentIndex 
                ? 'linear-gradient(to bottom, #FF6B35, #FF8F66)'
                : 'rgba(255, 255, 255, 0.15)',
              borderRadius: index === currentIndex ? '2px' : '50%',
              boxShadow: index === currentIndex ? '0 0 6px rgba(255,107,53,0.4)' : 'none',
              cursor: 'pointer'
            }}
            aria-label={`Go to post ${index + 1}`}
          />
        ))}
      </div>

      {/* Navigation Hint (only on root feed) */}
      {!canGoBack && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1.2, duration: 1 }}
          className="absolute left-1/2 -translate-x-1/2 z-40"
          style={{
            bottom: 'max(24px, calc(24px + env(safe-area-inset-bottom)))'
          }}
        >
          <div className="flex items-center gap-2 text-[10px] font-medium uppercase tracking-[0.15em] text-[#636366]">
            <span style={{ fontFamily: "'SF Mono', monospace" }}>SWIPE TO EXPLORE</span>
            <motion.svg
              animate={{ y: [0, 4, 0] }}
              transition={{ duration: 1.8, repeat: Infinity, ease: "easeInOut" }}
              className="w-3 h-3"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
              strokeWidth={2.5}
            >
              <path strokeLinecap="round" strokeLinejoin="round" d="M19 14l-7 7m0 0l-7-7m7 7V3" />
            </motion.svg>
          </div>
        </motion.div>
      )}
    </div>
  );
}
